import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import {
  whyCommerce, marketTrends, marketStats, courses,
  CATEGORY_ORDER, type CourseCategory,
} from '@/content/careers';

type Filter = CourseCategory | 'All';

/** Line-icon per course category — stroke inherits currentColor. */
function CategoryIcon({ category, className }: { category: CourseCategory; className?: string }) {
  const common = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  const paths: Record<CourseCategory, JSX.Element> = {
    Professional: <><path d="M12 3l7 4v5c0 4-3 6.5-7 8-4-1.5-7-4-7-8V7z" /><path d="M9 12l2 2 4-4" /></>,
    Degree: <><path d="M12 4l10 5-10 5L2 9z" /><path d="M6 11v5c0 1 3 3 6 3s6-2 6-3v-5" /></>,
    Law: <><path d="M12 4v16M7 20h10M5 7h14M5 7l-2 6h4zM19 7l-2 6h4z" /></>,
    Analytics: <><path d="M4 20V4M4 20h16M8 17v-5M12 17V8M16 17v-8" /></>,
    Creative: <><path d="M12 3a9 9 0 100 18c1.4 0 2-1 2-2 0-1.3-1-1.5-1-2.5 0-.8.7-1.5 1.5-1.5H17a4 4 0 004-4c0-4.4-4-8-9-8z" /><circle cx="7.5" cy="11.5" r="1" /><circle cx="12" cy="8" r="1" /><circle cx="16" cy="11" r="1" /></>,
    Other: <><circle cx="12" cy="12" r="9" /><path d="M12 7l2.5 4.5L12 16l-2.5-4.5z" /></>,
  };
  return <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...common}>{paths[category]}</svg>;
}

export function CareersPage() {
  const [filter, setFilter] = useState<Filter>('All');
  const present = CATEGORY_ORDER.filter((c) => courses.some((co) => co.category === c));
  const shown = filter === 'All' ? courses : courses.filter((c) => c.category === filter);

  return (
    <div>
      <SEO
        title="Careers after Commerce — Courses, Paths & Demand | ACES"
        description="A clear guide to where commerce can take you: why choose commerce, every major course after Class 12 (CA, CMA, CS, B.Com, BBA, Economics, Law, analytics & more) with what you study, eligibility, careers and India job-market demand."
        canonical="https://acescommerce.com/careers/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Careers after Commerce',
          description: 'Courses and career paths after Class 12 commerce in India.',
          url: 'https://acescommerce.com/careers/',
        }}
      />

      {/* ── Hero ── */}
      <section className="aces-hero">
        <div className="max-w-5xl mx-auto px-4 pt-16 sm:pt-20 pb-14">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-accent-600 mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-500" aria-hidden="true" />
            Career guidance
          </span>
          <h1 className="font-serif text-[2.4rem] sm:text-5xl lg:text-[3.4rem] text-ink-900 tracking-tight leading-[1.06] max-w-2xl text-balance">
            Where can commerce <em className="italic text-accent-600">take you?</em>
          </h1>
          <p className="mt-5 text-lg text-ink-600 max-w-2xl leading-relaxed">
            Commerce isn’t one road — it’s a junction. Here’s an honest map of the courses,
            careers and skills that open up after Class 12, and where India’s job market is heading.
          </p>

          <div className="mt-9 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {marketStats.map((s) => (
              <div key={s.label} className="aces-card px-5 py-5">
                <div className="font-serif text-[1.75rem] text-ink-900 leading-tight tracking-tight">{s.value}</div>
                <div className="text-xs text-ink-500 mt-1 leading-snug">{s.label}</div>
              </div>
            ))}
          </div>

          <p className="mt-4 text-xs text-ink-500">Market figures were checked on 25 October 2026 against ICAI, DPIIT, RBI and Zinnov-Nasscom publications.</p>
        </div>
      </section>

      {/* ── Why commerce ── */}
      <section className="bg-cream border-y border-ink-200">
        <div className="max-w-5xl mx-auto px-4 py-14">
          <h2 className="font-serif text-2xl md:text-3xl text-ink-900 tracking-tight">Why commerce is a smart choice</h2>
          <div className="mt-7 grid gap-5 md:grid-cols-3">
            {whyCommerce.map((p, i) => (
              <div key={i} className="relative pl-4">
                <span className="absolute left-0 top-1 bottom-1 w-[3px] rounded bg-accent-500" aria-hidden="true" />
                <p className="text-ink-700 leading-[1.7] text-[15.5px]">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Course explorer ── */}
      <section className="bg-ink-50">
        <div className="max-w-6xl mx-auto px-4 py-14">
          <div className="max-w-2xl">
            <h2 className="font-serif text-2xl md:text-3xl text-ink-900 tracking-tight">Courses after Class 12</h2>
            <p className="text-ink-500 mt-2 text-sm">Tap a course to see what you study, who it suits, where it leads, and how it’s valued. Filter by type:</p>
          </div>

          {/* Filter chips */}
          <div className="mt-6 flex flex-wrap gap-2">
            {(['All', ...present] as Filter[]).map((c) => {
              const active = filter === c;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => setFilter(c)}
                  aria-pressed={active}
                  className={[
                    'aces-chip',
                    active
                      ? 'aces-chip-active'
                      : '',
                  ].join(' ')}
                >
                  {c}
                </button>
              );
            })}
          </div>

          {/* Cards */}
          <div className="mt-7 grid gap-4 md:grid-cols-2">
            {shown.map((course) => (
              <details
                key={course.slug}
                className="group rounded-card border border-ink-200/80 bg-cream shadow-card hover:border-ink-300 open:shadow-card-raised open:border-accent-200 transition-[box-shadow,border-color] duration-200 [&_summary::-webkit-details-marker]:hidden"
              >
                <summary className="flex items-start gap-4 p-5 sm:p-6 cursor-pointer list-none rounded-card">
                  <span className="shrink-0 flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-accent-100/70 to-accent-50 text-accent-700 border border-accent-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]">
                    <CategoryIcon category={course.category} className="w-6 h-6" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2 flex-wrap">
                      <span className="font-serif text-[1.2rem] text-ink-900 tracking-tight">{course.name}</span>
                    </span>
                    <span className="block text-sm text-ink-600 mt-1 leading-snug">{course.tagline}</span>
                    <span className="mt-2.5 flex flex-wrap items-center gap-2 text-[11px]">
                      <span className="px-2 py-0.5 rounded-full bg-ink-100 text-ink-600 font-medium">{course.duration}</span>
                      <span className="px-2 py-0.5 rounded-full bg-ink-100 text-ink-600 font-medium">{course.level}</span>
                      <span className="px-2 py-0.5 rounded-full bg-ink-100 text-ink-500">{course.category}</span>
                    </span>
                  </span>
                  <span className="shrink-0 text-ink-400 group-open:rotate-180 transition-transform duration-200 mt-1" aria-hidden="true">⌄</span>
                </summary>

                <div className="px-5 sm:px-6 pb-6 pt-4 border-t border-ink-200/70 space-y-5">
                  <div>
                    <h3 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-accent-600 mb-2">What you study</h3>
                    <div className="flex flex-wrap gap-1.5">
                      {course.whatYouStudy.map((w) => (
                        <span key={w} className="text-xs text-ink-700 bg-ink-50 border border-ink-200 rounded-md px-2 py-1">{w}</span>
                      ))}
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <h3 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-accent-600 mb-1.5">Where it leads</h3>
                      <ul className="text-sm text-ink-700 space-y-1">
                        {course.careers.map((c) => (
                          <li key={c} className="flex gap-2"><span className="text-accent-500 mt-0.5" aria-hidden="true">→</span><span>{c}</span></li>
                        ))}
                      </ul>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <h3 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-500 mb-1">Who can do it</h3>
                        <p className="text-sm text-ink-700 leading-snug">{course.eligibility}</p>
                      </div>
                      <div>
                        <h3 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-500 mb-1">Market demand</h3>
                        <p className="text-sm text-ink-700 leading-snug">{course.demand}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </details>
            ))}
          </div>

          <p className="mt-6 text-xs text-ink-500 max-w-2xl leading-relaxed">
            Salary figures are typical starting or early-career pay in India, checked on 25 October 2026
            against PayScale, AmbitionBox, Indeed, institute reports and course-provider salary guides.
            Pay varies by employer, city and experience, so treat them as indicative rather than guaranteed.
          </p>
        </div>
      </section>

      {/* ── Professional courses deep-dive CTA ── */}
      <section className="bg-cream border-y border-ink-200">
        <div className="max-w-5xl mx-auto px-4 py-12">
          <div className="rounded-card-lg border border-accent-200/70 bg-gradient-to-br from-accent-50 via-cream to-ink-50 p-6 sm:p-9 shadow-card flex flex-col sm:flex-row sm:items-center gap-6">
            <div className="flex-1">
              <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-accent-600 mb-1.5">Going professional?</span>
              <h2 className="font-serif text-2xl text-ink-900 tracking-tight">ACCA · CMA · CA · US CPA — the full breakdown</h2>
              <p className="text-ink-600 mt-2 text-sm leading-relaxed max-w-xl">
                Honest, India-focused deep dives into each big credential: eligibility, cost,
                real starting salary, where you can work, and career growth.
              </p>
            </div>
            <Link
              to="/careers/courses/"
              className="aces-btn-primary shrink-0 px-5"
            >
              Explore the courses →
            </Link>
          </div>
        </div>
      </section>

      {/* ── Market trends ── */}
      <section className="bg-cream border-y border-ink-200">
        <div className="max-w-5xl mx-auto px-4 py-14">
          <h2 className="font-serif text-2xl md:text-3xl text-ink-900 tracking-tight">Where the market is heading</h2>
          <div className="mt-7 grid gap-4 sm:grid-cols-2">
            {marketTrends.map((t, i) => (
              <div key={i} className="flex gap-3.5 rounded-card border border-ink-200/80 bg-ink-50/70 px-5 py-4">
                <span className="shrink-0 font-serif text-accent-600 text-lg leading-none mt-0.5" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
                <p className="text-sm text-ink-700 leading-relaxed">{t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Closing CTA (indigo band) ── */}
      <section className="aces-band-night relative overflow-hidden bg-night-700">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.5] pointer-events-none"
          style={{
            backgroundImage:
              'repeating-linear-gradient(45deg, rgba(224,123,26,0.10) 0 1px, transparent 1px 16px), repeating-linear-gradient(-45deg, rgba(224,123,26,0.10) 0 1px, transparent 1px 16px)',
            WebkitMaskImage: 'linear-gradient(180deg, transparent, #000 30%, #000 70%, transparent)',
            maskImage: 'linear-gradient(180deg, transparent, #000 30%, #000 70%, transparent)',
          }}
        />
        <div className="relative max-w-3xl mx-auto px-4 py-14 text-center">
          <h2 className="font-serif text-2xl md:text-3xl text-white tracking-tight">Not sure which path yet?</h2>
          <p className="text-ink-100/80 mt-3 text-sm max-w-md mx-auto leading-relaxed">
            That’s completely normal at this stage. Start by getting genuinely good at the basics —
            the path becomes clear once a subject starts to click.
          </p>
          <Link
            to="/"
            className="aces-btn-primary mt-7"
          >
            Start learning — free →
          </Link>
        </div>
      </section>
    </div>
  );
}
